import { UPLOAD_ITEM_STATUS } from './uploadStatuses';
import { UploadItem } from './uploadItem';
import { getFormatedSize } from '../utils/parseUtils';
import { UploadService } from '../services/uploader.service';


const SPEED_UPDATE_INTERVAL = 20000;

interface UploadDir {
    name: string;
    fullPath: string;
    speed: string | void;
    state: string;
    files: Array<UploadItem>;
}

export class UploadCollection {
    // Files and dirs a stored separetly to easier handle it
    private _standAlonefiles: Array<UploadItem> = [];
    private _dirs: object = {};

    public get dirs() {
        return Object.values(this._dirs)
    }

    public get standAloneFiles() {
        return this._standAlonefiles
    }

    // Files upload like dirs
    private get filesFromDirs() {
        return this.dirs.map((dir: UploadDir) => dir.files).reduce((acc, val) => acc.concat(val), [])
    }
    // We use items only for view part
    public get items() {
        return [...this.dirs, ...this._standAlonefiles]
    }

    // All files which should be uploaded, !dirs skiped
    public get files() {
        return [...this.filesFromDirs, ... this._standAlonefiles]
    }

    public hasActiveUpload = false;

    constructor(private uploadService: UploadService) {

    }

    private prepareFile({ originalFile: file, fullPath }) {
        const { name, size } = file
        return {
            name,
            size, // In Bytes
            fullPath, // Full path including folders
            received: 0, // Allready sent bytes
            chunkUploadSpeed: 0, // Upload speed
            uploadURL: '', // we use it in query param at upload
            start: 0,
            end: 0,
            lastModified: Date.now(),
            totalUploaded: 0,
            xhr: null, // Http request for cancel action
            speed: '',
            errorMsg: '', // Text for tooltip if error occured
            chunkSize: null,
            state: this.hasActiveUpload ? UPLOAD_ITEM_STATUS.UPLOAD_STARTED : UPLOAD_ITEM_STATUS.UPLOAD_QUEUED,
            _originalFile: file // File from input
        }
    }

    // Add single file
    public addStandAloneFile(file) {
        this._standAlonefiles.push(this.prepareFile(file))
    }

    // Create new dir
    public mkdir({ name, fullPath }) {
        this._dirs[name] = ({
            name,
            fullPath,
            files: [],
            state: this.hasActiveUpload ? UPLOAD_ITEM_STATUS.UPLOAD_STARTED : UPLOAD_ITEM_STATUS.UPLOAD_QUEUED,
            speed: null,
            get size() {
                return this.files.reduce((acc, file) => acc + file.size, 0)
            },
            get received() {
                return this.files.reduce((acc, file) => acc + file.received, 0)
            }
        })

    }

    // We need update dir state according to it child status
    // to display it for users
    private updateDirectoryStatus(dirName) {
        const dir = this._dirs[dirName]
        if (!dir) {
            return
        }
        const childStatuses = dir.files.map(item => item.state)
        if (childStatuses.includes(UPLOAD_ITEM_STATUS.UPLOAD_CANCELED) || dir.state === UPLOAD_ITEM_STATUS.UPLOAD_CANCELED) {
            dir.state = UPLOAD_ITEM_STATUS.UPLOAD_CANCELED;
            dir.files.forEach(file => {
                file.state = UPLOAD_ITEM_STATUS.UPLOAD_CANCELED;
                if (file.xhr) {
                    file.xhr.unsubscribe()
                }
                if (file.cancelSource) {
                    file.cancelSource.next()
                }
            })
            this.uploadService.startUpload();
            return
        }
        if (childStatuses.includes(UPLOAD_ITEM_STATUS.UPLOAD_UPLOADING)) {
            dir.state = UPLOAD_ITEM_STATUS.UPLOAD_UPLOADING;
            const activeFile = dir.files.find(file => file.state === UPLOAD_ITEM_STATUS.UPLOAD_UPLOADING)
            if (activeFile.chunkUploadSpeed) {
                dir.speed = activeFile.speed;
            }
            return
        }
        if (childStatuses.includes(UPLOAD_ITEM_STATUS.UPLOAD_ERROR)) {
            dir.state = UPLOAD_ITEM_STATUS.UPLOAD_ERROR
            const target = dir.files.find(file => file.state === UPLOAD_ITEM_STATUS.UPLOAD_ERROR && file.errorMsg)
            dir.errorMsg = target ? target.errorMsg : null;
            return
        }
        if (childStatuses.includes(UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR)) {
            dir.state = UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR
            return
        }
        if (childStatuses.includes(UPLOAD_ITEM_STATUS.UPLOAD_FINISHED)) {
            dir.state = UPLOAD_ITEM_STATUS.UPLOAD_FINISHED
            return
        }
        dir.state = UPLOAD_ITEM_STATUS.UPLOAD_STARTED
    }

    // Add file for existing folder
    public addFileToFolder(file, parent) {
        this._dirs[parent].files.push({ ...this.prepareFile(file), parent })

    }

    // Removes all files and directories
    public clear() {
        this._dirs = {};
        this._standAlonefiles.length = 0;
    }

    // Remove specific item
    public remove(item) {
        if (item.files) {
            delete this._dirs[item.name]
        } else {
            this._standAlonefiles.splice(this._standAlonefiles.indexOf(item), 1)
        }
    }

    // We need to react on file state change
    // so change file.state only using it method!
    public changeFileState(file, newState) {
        if (file.state === UPLOAD_ITEM_STATUS.UPLOAD_UPLOADING && newState === UPLOAD_ITEM_STATUS.UPLOAD_CANCELED) {
            file.speed = null;
            file.state = newState;
            if (file.files) {
                file.files[0].state = newState;
                this.updateDirectoryStatus(file.parent);
            } else {
                if (file.xhr) {
                    file.xhr.unsubscribe();
                }
                if (file.cancelSource) {
                    file.cancelSource.next();
                }
                file.speed = null;
                this.uploadService.startUpload();
            }
            return
        }

        if (file.state === UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR && newState === UPLOAD_ITEM_STATUS.UPLOAD_STARTED) {
            file.state = UPLOAD_ITEM_STATUS.UPLOAD_STARTED;
        }

        if (newState === UPLOAD_ITEM_STATUS.UPLOAD_ERROR) {
            file.speed = null;
            file.chunkUploadSpeed = 0;
        }
        if (
            newState === UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR &&
            file.errorMsg
        ) {
            file.speed = null;
            file.chunkUploadSpeed = 0;
        }

        file.state = newState;

        if (file.state === UPLOAD_ITEM_STATUS.UPLOAD_CANCELED) {
            if (!file.xhr) {
                this.remove(file)
            }
            file.speed = null;
        }

        if (file.state === UPLOAD_ITEM_STATUS.UPLOAD_UPLOADING) {
            if (file.chunkUploadSpeed) {
                file.speed = `${getFormatedSize(file.chunkUploadSpeed)}/sek`;
            } else if (file.chunkSize / (SPEED_UPDATE_INTERVAL / 1000)) {
                file.speed = `${getFormatedSize(file.chunkSize / (SPEED_UPDATE_INTERVAL / 1000))}/sek`;
            } else {
                file.speed = null;
            }
        }
        // if file is from directory, we need to update dir status
        if (file.parent) {
            this.updateDirectoryStatus(file.parent)
        }
    }

    public getNextFile() {
        return this.files.find(item => item.state === UPLOAD_ITEM_STATUS.UPLOAD_STARTED);
    };
}
